/**
 * @file MlrReportDocument.tsx
 * @brief Full 13-section MLR as stacked A4 pages matching the exported PDF.
 *
 * @details
 * Outline jumps to the A4 sheet (not the inner heading) and aligns under
 * the visible format bar; the current sheet stays highlighted in Pages.
 * Save persists statements, examination, MLR supplements, identity, and
 * prognosis wording in one action. Changing the claimant name in §1
 * rewrites that name in live §2 copy and in narrative drafts (on blur /
 * save). Paragraphs use TipTap (bold / italic / underline / lists);
 * PDF/XML export strips markup. Format/save controls portal into the
 * page sticky strip (`mlr-editor-chrome`) so they pin as chrome, not a chip
 * over the paper (08-09-2026).
 *
 * @references
 * - docs/product/medical-assessment.md
 * - BACKEND/src/features/medical-assessment/export/mlrPdf.builder.mjs
 * - BACKEND/src/features/medical-assessment/export/stripMlrMarkup.mjs
 *
 * @author DES
 * @date 08-09-2026
 * @updated 18-09-2026 — coverage callout: complete copy, chevron, flush strip
 */

import { SkeletonFields } from "@/components/ui/Skeleton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  CircleAlert,
  LoaderCircle,
  Lock,
  Save,
  Scale,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ApiClientError } from "@/shared/api/client";
import { useIsFetching } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { scrollMainToSection } from "@/shared/scroll/scrollMainTo";
import { useMainScroll } from "@/shared/scroll/main-scroll-context";
import {
  medicalAssessmentKeys,
  useAmendReport,
  useAutoGenerateReport,
  useCoverage,
  useVoidReportSignature,
  useConfirmPrognosis,
  useExamination,
  useMlrSupplements,
  usePatchCaseHubIdentity,
  usePrognoses,
  useReportSections,
  useUpdateAssessmentStatement,
  useUpsertExamination,
  useUpsertMlrSupplements,
  useAnswerPrognosisDecision,
  useVerifyStatement,
  useDeleteStatement,
} from "../api";
import type {
  AssessmentStatement,
  CaseHub,
  MlrClassificationAnswer,
  MedicalAssessment,
  MedicalRecordsEntry,
  MlrCoverage,
  MlrEditableTarget,
  MlrClaimantWarning,
  MlrIdentityRow,
  MlrReportSection,
} from "../schemas";
import { useAuth } from "@/shared/auth/auth-context";
import {
  formatClaimantNameFromHub,
  formatMlrShortDate,
  MLR_IDENTITY_LABEL,
  patchIdentityValue,
} from "../mlrIdentityFields";
import {
  inferStaleClaimantNames,
  rewriteClaimantMentionsMany,
  rewriteStringMapMany,
  uniqueClaimantNames,
} from "../mlrClaimantRename";
import { assessmentClaimantName } from "../assessmentListDisplay";
import { htmlToPlain, isRichTextEmpty, richTextEqual } from "../mlrRichText";
import {
  mlrPageFromAnchorId,
  mlrSectionAnchor,
  useMlrActivePage,
  type MlrNavPage,
} from "../useMlrActivePage";
import {
} from "../prognosisStructuredFields";
import { DocParagraph } from "./DocParagraph";
import { MlrRecordsReviewPanel } from "./MlrRecordsReviewPanel";
import { MlrDocBlocks, type MlrBlockBinding } from "./MlrDocBlocks";
import {
  MLR_EDITOR_CHROME_ID,
  MlrEditorChromePortal,
} from "./MlrEditorChromePortal";
import { MlrFormatToolbar } from "./MlrFormatToolbar";
import { MlrRichTextProvider } from "./MlrRichTextProvider";
import {
  MlrContentsPage,
  MlrCoverPage,
  MlrNumberLabel,
  MlrSectionHeading,
  MlrSheet,
  MlrStaticParagraph,
  MlrSubheading,
} from "./MlrPrintChrome";
import { StatementOfTruthReview } from "./StatementOfTruthReview";
import type { MedicalRecordsEntryInput } from "./MedicalRecordsEntriesEditor";

type RegistrationDraft = {
  gmcNumber: string;
  mpsNumber: string;
  icoReference: string;
  medcoId: string;
  medcoTrainingDate: string;
  currentPositions: string;
  experience: string;
};

/*
 * ⚠️ EMPTY, and that is the fix.
 *
 * These were "Yes", "Yes", "Yes" — and the PDF builder defaulted to "Yes"
 * again on its own. §12.1 decides whether the whiplash tariff applies and
 * prints directly above the Statement of Truth, so a report nobody had
 * answered asserted three things about money in the doctor's name.
 *
 * An empty string here means **the doctor has not answered**. What §12.1 shows
 * then is whatever the rulebook or intake settled, and a blank when neither
 * did. (18-09-2026)
 */
const DEFAULT_CLASSIFICATION = {
  motorVehicleOccupant: "",
  softTissueInjuryClaim: "",
  firstReportInRelationToIncident: "",
};

type ClassificationDraft = typeof DEFAULT_CLASSIFICATION;

/** Plain names for the coverage checklist — the section ids are internal. */
/**
 * The facts that settled a classification, in words a doctor reads rather than
 * field paths.
 *
 * The rule records `symptoms.causingInjuriesTo = "neck"`; a doctor should see
 * "the injury was to the neck". Deliberately small — an unmapped path falls
 * back to its value, which is still truer than hiding it.
 */
const BECAUSE_PHRASE: Record<string, (value: unknown) => string> = {
  "accident.accidentType": (v) => String(v).toLowerCase(),
  "accident.claimantPosition": (v) => `the claimant was ${String(v).toLowerCase()}`,
  "symptoms.causingInjuriesTo": (v) => `the injury was to the ${String(v).toLowerCase()}`,
};

function describeBecause(
  // `z.unknown()` makes the key optional in the inferred type, so the shape is
  // taken as the schema hands it over rather than re-asserted here.
  because: Array<{ path: string; actual?: unknown }>,
): string {
  const parts = because.map(({ path, actual }) =>
    BECAUSE_PHRASE[path] ? BECAUSE_PHRASE[path](actual) : String(actual),
  );
  if (parts.length <= 1) return parts[0] ?? "the recorded facts";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

const COVERAGE_SECTION_LABEL: Record<string, string> = {
  accident: "The accident",
  symptoms: "Injuries and symptoms",
  treatment: "Treatment",
  work: "Work",
  travel: "Travel anxiety",
  homelife: "Home life",
  pmh: "Past medical history",
  // §7.3 — not a history section. It is answered by what the doctor did, not
  // by what the claimant said, and arrives from the examination grid.
  examination: "Examination",
};

/**
 * What the consultation never asked. Native `<details>`/`<summary>` with
 * `display:flex` hides the disclosure marker and leaves "were not asked about"
 * hanging — a truncated sentence with the count stranded on the far right.
 * A button + chevron matches the mobile card and the rest of this panel
 * (18-09-2026). Flush under the editor chrome because GridCell is unpadded.
 */
function MlrCoverageCallout({
  coverage,
  warnings = [],
}: {
  coverage: MlrCoverage;
  warnings?: MlrClaimantWarning[];
}) {
  /*
   * ⚠️ `missingCount` means "not extracted", and this callout has always read
   * it aloud as "not asked". On a real consultation that was 39 questions when
   * 32 had been asked and answered. `notAsked` is the server's narrowed count;
   * the fallback keeps an older server rendering. (18-09-2026)
   */
  const notAsked = coverage.notAsked ?? coverage.missingCount;

  /*
   * ⚠️ The claimant's own record contradicting itself — "Mrs Ishaq … he lives
   * with his wife". Neither field is wrong alone, and no builder is wrong; the
   * contradiction lives between them, which is why nothing owned it. It is a
   * SIBLING of the checklist, not nested in it: stuffing both into the amber
   * strip made the missed questions look like a child of the contradiction.
   * (18-09-2026)
   */
  const [open, setOpen] = useState(false);
  const missingSections = coverage.sections
    .map((section) => ({ ...section, gaps: section.notAsked ?? section.missing }))
    .filter((section) => section.gaps.length > 0);
  const coveredRatio =
    coverage.applicable === 0
      ? 0
      : Math.min(100, (coverage.answered / coverage.applicable) * 100);

  return (
    <>
      {warnings.length > 0 ? (
        <div className="border-b border-primary/30 bg-primary/5 px-4 py-3 md:px-5">
          {warnings.map((warning) => (
            <p key={warning.code} className="text-sm text-foreground">
              <span className="font-medium">
                {warning.code === "TITLE_SEX_MISMATCH"
                  ? "Title and sex do not agree. "
                  : warning.code === "CLAIMANT_NAME_MISSING"
                    ? "Claimant has no name. "
                    : null}
              </span>
              {warning.message}
            </p>
          ))}
        </div>
      ) : null}
      {notAsked <= 0 ? null : (
        <div className="border-b border-warning/30 bg-warning/5">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-warning/10 md:px-5"
          >
            <CircleAlert
              className="mt-0.5 h-4 w-4 shrink-0 text-warning"
              aria-hidden
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-foreground">
                {notAsked}{" "}
                {notAsked === 1 ? "question was" : "questions were"}{" "}
                not asked
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {open
                  ? "The report will print without these, and will look complete."
                  : `${coverage.answered} of ${coverage.applicable} covered. Click to see what is missing.`}
              </span>
            </span>
            <ChevronDown
              className={cn(
                "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
                open && "rotate-180",
              )}
              aria-hidden
            />
          </button>
          <div
            className="h-0.5 bg-warning/15"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={coverage.applicable}
            aria-valuenow={coverage.answered}
            aria-label="Questions covered"
          >
            <div
              className="h-full bg-warning"
              style={{ width: `${coveredRatio}%` }}
            />
          </div>
          {open ? (
            <div className="px-4 py-3 md:px-5">
              <div className="grid gap-3 sm:grid-cols-2">
                {missingSections.map((section) => (
                  <div key={section.section}>
                    <p className="text-xs font-medium text-muted-foreground">
                      {COVERAGE_SECTION_LABEL[section.section] ?? section.section}
                      {section.absent ? " — not discussed at all" : ""}
                    </p>
                    <ul className="mt-1 space-y-0.5 text-sm">
                      {section.gaps.map((gap) => (
                        <li key={gap.key}>• {gap.question}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}

type DocumentDrafts = {
  statements: Record<string, string>;
  generalFindings: string;
  // §7.2. The column and the PDF have always had it; only the panel did not,
  // so a presentation finding recorded on the phone was invisible here and
  // uneditable. (13-09-2026)
  presentationFindings: string;
  findingsByInjury: Record<string, string>;
  medicalRecordsSummary: string;
  medicalRecordsReview: string;
  medicalRecordsEntries: MedicalRecordsEntryInput[];
  prognosisOpinion: string;
  specialConsiderations: string;
  futureTreatment: string;
  qualifications: string;
  /** §4 closing — the physician's view of the claimant's account. */
  accountAssessment: string;
  registrationDetails: RegistrationDraft;
  caseClassification: ClassificationDraft;
  /**
   * §9's three answers, keyed by the supplement field each one saves to.
   * "" means the doctor has not answered — the report prints the derivation
   * or, for their own question, a blank rule. Never "No".
   */
  exceptionalSeverity: Record<string, string>;
  prognoses: Record<string, string>;
  identity: Record<string, string>;
};

type Props = {
  assessmentId: string;
  assessment: MedicalAssessment;
  statements: AssessmentStatement[];
  caseHub?: CaseHub;
  onSaved?: () => void;
  showSignedEditHint?: boolean;
};

function mapEntries(
  entries: MedicalRecordsEntry[] | undefined,
): MedicalRecordsEntryInput[] {
  return (entries ?? []).map((entry) => ({
    phase: entry.phase,
    source: entry.source,
    dateLabel: entry.dateLabel ?? "",
    narrative: entry.narrative,
    footnote: entry.footnote ?? "",
  }));
}

function entriesEqual(
  a: MedicalRecordsEntryInput[],
  b: MedicalRecordsEntryInput[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((entry, index) => {
    const other = b[index];
    return (
      entry.phase === other.phase &&
      entry.source === other.source &&
      entry.dateLabel === other.dateLabel &&
      entry.footnote === other.footnote &&
      richTextEqual(entry.narrative, other.narrative)
    );
  });
}

function textMapDirty(
  a: Record<string, string>,
  b: Record<string, string>,
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (!richTextEqual(a[key] ?? "", b[key] ?? "")) return true;
  }
  return false;
}

function concatNarrativeDrafts(drafts: DocumentDrafts): string {
  return [
    ...Object.values(drafts.statements),
    drafts.generalFindings,
    drafts.presentationFindings,
    ...Object.values(drafts.findingsByInjury),
    drafts.medicalRecordsSummary,
    drafts.medicalRecordsReview,
    ...drafts.medicalRecordsEntries.map(
      (entry) => `${entry.narrative}\n${entry.footnote}`,
    ),
    drafts.prognosisOpinion,
    drafts.specialConsiderations,
    drafts.futureTreatment,
    drafts.accountAssessment,
    ...Object.values(drafts.prognoses),
  ].join("\n");
}

function applyClaimantRenameToDrafts(
  drafts: DocumentDrafts,
  fromName: string,
  toName: string,
): DocumentDrafts {
  const to = toName.trim();
  if (!to) return drafts;
  const aliases = uniqueClaimantNames([
    fromName,
    ...inferStaleClaimantNames(concatNarrativeDrafts(drafts), to),
  ]).filter((name) => name.toLowerCase() !== to.toLowerCase());
  if (aliases.length === 0) return drafts;
  return {
    ...drafts,
    statements: rewriteStringMapMany(drafts.statements, aliases, to),
    generalFindings: rewriteClaimantMentionsMany(
      drafts.generalFindings,
      aliases,
      to,
    ),
    presentationFindings: rewriteClaimantMentionsMany(
      drafts.presentationFindings,
      aliases,
      to,
    ),
    findingsByInjury: rewriteStringMapMany(
      drafts.findingsByInjury,
      aliases,
      to,
    ),
    medicalRecordsSummary: rewriteClaimantMentionsMany(
      drafts.medicalRecordsSummary,
      aliases,
      to,
    ),
    medicalRecordsReview: rewriteClaimantMentionsMany(
      drafts.medicalRecordsReview,
      aliases,
      to,
    ),
    medicalRecordsEntries: drafts.medicalRecordsEntries.map((entry) => ({
      ...entry,
      narrative: rewriteClaimantMentionsMany(entry.narrative, aliases, to),
      footnote: rewriteClaimantMentionsMany(entry.footnote, aliases, to),
    })),
    prognosisOpinion: rewriteClaimantMentionsMany(
      drafts.prognosisOpinion,
      aliases,
      to,
    ),
    specialConsiderations: rewriteClaimantMentionsMany(
      drafts.specialConsiderations,
      aliases,
      to,
    ),
    // Names the claimant in every sentence it produces, so a rename that
    // skipped it would leave §4 half on the old name. (16-09-2026)
    accountAssessment: rewriteClaimantMentionsMany(
      drafts.accountAssessment,
      aliases,
      to,
    ),
    futureTreatment: rewriteClaimantMentionsMany(
      drafts.futureTreatment,
      aliases,
      to,
    ),
    prognoses: rewriteStringMapMany(drafts.prognoses, aliases, to),
  };
}

const TOTAL_PAGES = 15;

function sectionPageNumber(sectionNumber: number): number {
  return sectionNumber + 2;
}

function mlrNavItemClass(active: boolean): string {
  return cn(
    "w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors",
    active
      ? "bg-muted font-semibold text-foreground ring-1 ring-inset ring-border"
      : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
  );
}

/**
 * §12.1's three questions, in the order the report prints them.
 *
 * One copy, because the rows that ask them and the gate that refuses a
 * signature until they are answered must not be able to disagree about what
 * "answered" means.
 */
const CLASSIFICATION_QUESTIONS = [
  [
    "motorVehicleOccupant",
    "Was the Claimant an occupant of a motor vehicle?",
  ],
  [
    "softTissueInjuryClaim",
    "Is the client's most significant injury a soft-tissue injury?",
  ],
  [
    "firstReportInRelationToIncident",
    "Is this the first report in relation to the client's injuries from the index incident?",
  ],
] as const;

/**
 * One §12.1 answer on the paper.
 *
 * ⚠️ This was a `<select>` — three of them, always on screen, every one
 * pre-set to "Yes". That is the desktop tool this module exists to replace:
 * a control that asks a doctor to restate what the consultation already
 * settled, and answers for them when they do not.
 *
 * What replaces it is not a prettier control. Two of these questions are
 * decided — the rulebook knows whether the claimant was in a vehicle, and it
 * has already classified every injury — and the third comes from the
 * instructing party. So the answer is printed as the report prints it, with
 * the reason under it, and the only control is the one that disagrees.
 *
 * When nothing decided it, the two answers appear as a question — once, on
 * the page, in the doctor's way — rather than a field pre-filled with one of
 * them. (18-09-2026)
 */
function ClassificationAnswer({
  question,
  answered,
  resolved,
  onAnswer,
}: {
  question: string;
  /** The doctor's own answer, if they have given one. */
  answered: string;
  resolved?: MlrClassificationAnswer;
  onAnswer: (value: string) => void;
}) {
  const value = answered || resolved?.value || "";
  const source = answered ? "doctor" : (resolved?.source ?? null);
  const reason = answered
    ? null
    : (resolved?.cite ?? resolved?.because?.[0] ?? null);

  if (!value) {
    return (
      <div className="mt-2">
        <MlrStaticParagraph>{question}</MlrStaticParagraph>
        <div className="mt-1 flex items-center gap-2">
          {["Yes", "No"].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onAnswer(option)}
              className="rounded-none border border-[#1a1a1a] bg-transparent px-3 py-0.5 text-[10pt] text-[#1a1a1a] transition-colors hover:bg-[#1a1a1a] hover:text-white"
              style={{ fontFamily: '"Times New Roman", Times, Georgia, serif' }}
            >
              {option}
            </button>
          ))}
          {/* Nothing prints until one of them is pressed — the PDF shows the
              same blank rule the signature line uses. */}
          <span className="font-[family-name:Times_New_Roman,Times,serif] text-[9pt] italic text-[#666666]">
            Not answered — prints blank
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <MlrStaticParagraph>{question}</MlrStaticParagraph>
      <MlrStaticParagraph>{value}</MlrStaticParagraph>
      <p className="font-[family-name:Times_New_Roman,Times,serif] text-[9pt] italic text-[#666666]">
        {source === "doctor" ? "Your answer" : reason || "Derived"}
        {" · "}
        <button
          type="button"
          onClick={() => onAnswer(value === "Yes" ? "No" : "Yes")}
          className="underline underline-offset-2 hover:text-[#1a1a1a]"
        >
          Change to {value === "Yes" ? "No" : "Yes"}
        </button>
      </p>
    </div>
  );
}

export function MlrReportDocument({
  assessmentId,
  assessment,
  statements,
  caseHub,
  onSaved,
  showSignedEditHint = false,
}: Props) {
  const examination = useExamination(assessmentId);
  const supplements = useMlrSupplements(assessmentId);
  const prognoses = usePrognoses(assessmentId);
  const reportSections = useReportSections(assessmentId);
  const verifyStatement = useVerifyStatement(assessmentId);
  const answerDecision = useAnswerPrognosisDecision(assessmentId);
  const deleteStatement = useDeleteStatement(assessmentId);
  const updateStatement = useUpdateAssessmentStatement(assessmentId);
  const saveExamination = useUpsertExamination(assessmentId);
  const saveSupplements = useUpsertMlrSupplements(assessmentId);
  const patchIdentity = usePatchCaseHubIdentity(assessmentId);
  const confirmPrognosis = useConfirmPrognosis(assessmentId);
  const autoGenerate = useAutoGenerateReport(assessmentId);
  const coverage = useCoverage(assessmentId);
  const voidSignature = useVoidReportSignature(assessmentId);
  const amend = useAmendReport(assessmentId);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pauseChromeCollapse } = useMainScroll();
  const [paperStack, setPaperStack] = useState<HTMLElement | null>(null);
  const pagesNavRef = useRef<HTMLElement | null>(null);
  const [pinnedPage, setPinnedPage] = useState<MlrNavPage | null>(null);
  const spyPage = useMlrActivePage(paperStack);
  const activePage = pinnedPage ?? spyPage;
  const [drafts, setDrafts] = useState<DocumentDrafts | null>(null);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const narrativesNameRef = useRef("");
  const staleNameHealKeyRef = useRef("");

  /*
   * ⚠️ `sourceDrafts` is assembled from SIX queries — the detail (which
   * carries the statements), the examination, the supplements, the prognoses
   * and the report sections. A save invalidates all of them, and they come
   * back one at a time, so for a moment the drafts are half old and half new:
   * §1 can already say the new claimant name while §3 still says the old one,
   * or the reverse.
   *
   * Both effects below act on the claimant's NAME, which is exactly the field
   * that differs during that window — so both wait for the cache to settle.
   * `detail(assessmentId)` is a prefix of every one of those query keys.
   * (18-09-2026)
   */
  const refreshing = useIsFetching({
    queryKey: medicalAssessmentKeys.detail(assessmentId),
  });
  const settled = refreshing === 0;

  const exam = examination.data;
  const mlr = supplements.data;
  const prognosisRows = useMemo(
    () => prognoses.data ?? [],
    [prognoses.data],
  );

  const identityRows = useMemo<MlrIdentityRow[]>(() => {
    const section1 = reportSections.data?.sections?.find(
      (section) => section.number === 1,
    );
    const block = section1?.blocks.find(
      (item) => item.type === "identityTable",
    );
    return block && block.type === "identityTable" ? block.rows : [];
  }, [reportSections.data]);

  const sourceDrafts = useMemo((): DocumentDrafts => {
    const statementMap: Record<string, string> = {};
    for (const row of statements) {
      statementMap[row.id] = row.details ?? "";
    }
    // No pre-seeding of empty injury keys: §7's editor slots arrive with the
    // model, and a key is created when the doctor types into one.
    const findings = { ...(exam?.findingsByInjury ?? {}) };
    const prognosisMap: Record<string, string> = {};
    for (const row of prognosisRows) {
      prognosisMap[row.id] = row.confirmedText ?? row.aiDraftText ?? "";
    }
    return {
      statements: statementMap,
      generalFindings: exam?.generalFindings ?? "",
      presentationFindings: exam?.presentationFindings ?? "",
      findingsByInjury: findings,
      medicalRecordsSummary: mlr?.medicalRecordsSummary ?? "",
      medicalRecordsReview: mlr?.medicalRecordsReview ?? "",
      medicalRecordsEntries: mapEntries(mlr?.medicalRecordsEntries),
      prognosisOpinion: mlr?.prognosisOpinion ?? "",
      specialConsiderations: mlr?.specialConsiderations ?? "",
      futureTreatment: mlr?.futureTreatment ?? "",
      qualifications: mlr?.qualifications ?? "",
      accountAssessment: mlr?.accountAssessment ?? "",
      registrationDetails: {
        gmcNumber: mlr?.registrationDetails?.gmcNumber ?? "",
        mpsNumber: mlr?.registrationDetails?.mpsNumber ?? "",
        icoReference: mlr?.registrationDetails?.icoReference ?? "",
        medcoId: mlr?.registrationDetails?.medcoId ?? "",
        medcoTrainingDate: mlr?.registrationDetails?.medcoTrainingDate ?? "",
        currentPositions: mlr?.registrationDetails?.currentPositions ?? "",
        experience: mlr?.registrationDetails?.experience ?? "",
      },
      caseClassification: {
        motorVehicleOccupant:
          mlr?.caseClassification?.motorVehicleOccupant ??
          DEFAULT_CLASSIFICATION.motorVehicleOccupant,
        softTissueInjuryClaim:
          mlr?.caseClassification?.softTissueInjuryClaim ??
          DEFAULT_CLASSIFICATION.softTissueInjuryClaim,
        firstReportInRelationToIncident:
          mlr?.caseClassification?.firstReportInRelationToIncident ??
          DEFAULT_CLASSIFICATION.firstReportInRelationToIncident,
      },
      exceptionalSeverity: {
        exceptionalWhiplashReported: mlr?.exceptionalWhiplashReported ?? "",
        exceptionalCircumstancesReported:
          mlr?.exceptionalCircumstancesReported ?? "",
        exceptionalSeverityAgreed: mlr?.exceptionalSeverityAgreed ?? "",
      },
      prognoses: prognosisMap,
      // The identity sheet arrives assembled in §1 — same rows, same order,
      // same formatting as the PDF prints. The panel no longer recomputes it
      // from the Case Hub, which is how its copy of the 18-label list came to
      // exist in the first place. (13-09-2026)
      /*
       * Keyed by `row.key`, the CANONICAL label — not by `row.label`, which is
       * what this report's shape prints. They are the same string on an RTA
       * report and differ on an exposure one ("Date of manifestation"), and the
       * saved `identitySheet` is keyed by the canonical string on both sides.
       * Keying drafts by the printed label would silently orphan every saved
       * overlay on a non-RTA report. `?? row.label` covers a payload from a
       * backend that predates `key`. (19-09-2026)
       */
      identity: Object.fromEntries(
        identityRows.map((row) => [row.key ?? row.label, row.value]),
      ),
    };
  }, [statements, exam, mlr, prognosisRows, identityRows]);

  const dirty = useMemo(() => {
    if (!drafts) return false;
    if (textMapDirty(drafts.statements, sourceDrafts.statements)) return true;
    if (!richTextEqual(drafts.generalFindings, sourceDrafts.generalFindings)) {
      return true;
    }
    if (
      !richTextEqual(
        drafts.presentationFindings,
        sourceDrafts.presentationFindings,
      )
    ) {
      return true;
    }
    if (textMapDirty(drafts.findingsByInjury, sourceDrafts.findingsByInjury)) {
      return true;
    }
    if (
      !richTextEqual(
        drafts.medicalRecordsSummary,
        sourceDrafts.medicalRecordsSummary,
      ) ||
      !richTextEqual(
        drafts.medicalRecordsReview,
        sourceDrafts.medicalRecordsReview,
      ) ||
      !richTextEqual(drafts.prognosisOpinion, sourceDrafts.prognosisOpinion) ||
      !richTextEqual(
        drafts.specialConsiderations,
        sourceDrafts.specialConsiderations,
      ) ||
      !richTextEqual(drafts.futureTreatment, sourceDrafts.futureTreatment) ||
      !richTextEqual(drafts.qualifications, sourceDrafts.qualifications) ||
      !richTextEqual(drafts.accountAssessment, sourceDrafts.accountAssessment)
    ) {
      return true;
    }
    if (!entriesEqual(drafts.medicalRecordsEntries, sourceDrafts.medicalRecordsEntries)) {
      return true;
    }
    if (
      !richTextEqual(
        drafts.registrationDetails.gmcNumber,
        sourceDrafts.registrationDetails.gmcNumber,
      ) ||
      !richTextEqual(
        drafts.registrationDetails.mpsNumber,
        sourceDrafts.registrationDetails.mpsNumber,
      ) ||
      !richTextEqual(
        drafts.registrationDetails.icoReference,
        sourceDrafts.registrationDetails.icoReference,
      ) ||
      !richTextEqual(
        drafts.registrationDetails.medcoId,
        sourceDrafts.registrationDetails.medcoId,
      ) ||
      !richTextEqual(
        drafts.registrationDetails.medcoTrainingDate,
        sourceDrafts.registrationDetails.medcoTrainingDate,
      ) ||
      !richTextEqual(
        drafts.registrationDetails.currentPositions,
        sourceDrafts.registrationDetails.currentPositions,
      ) ||
      !richTextEqual(
        drafts.registrationDetails.experience,
        sourceDrafts.registrationDetails.experience,
      ) ||
      JSON.stringify(drafts.caseClassification) !==
        JSON.stringify(sourceDrafts.caseClassification)
    ) {
      return true;
    }
    if (textMapDirty(drafts.prognoses, sourceDrafts.prognoses)) {
      return true;
    }
    if (textMapDirty(drafts.identity, sourceDrafts.identity)) {
      return true;
    }
    return false;
  }, [drafts, sourceDrafts]);

  const needsPrognosisConfirm = Boolean(
    drafts &&
      prognosisRows.some(
        (row) =>
          row.status !== "confirmed" &&
          !isRichTextEmpty(drafts.prognoses[row.id] ?? ""),
      ),
  );

  useEffect(() => {
    if (touched || !settled) return;
    const toName = sourceDrafts.identity[MLR_IDENTITY_LABEL.fullName] ?? "";
    const healed = applyClaimantRenameToDrafts(
      sourceDrafts,
      narrativesNameRef.current,
      toName,
    );
    setDrafts(healed);
    narrativesNameRef.current = toName;
  }, [sourceDrafts, touched, settled]);

  useEffect(() => {
    staleNameHealKeyRef.current = "";
  }, [assessmentId]);

  useEffect(() => {
    if (touched || saving || !settled) return;
    const toName = sourceDrafts.identity[MLR_IDENTITY_LABEL.fullName] ?? "";
    if (!toName) return;
    const stale = inferStaleClaimantNames(
      concatNarrativeDrafts(sourceDrafts),
      toName,
    );
    if (stale.length === 0) return;
    const healKey = `${assessmentId}:${toName}:${stale.join("|")}`;
    if (staleNameHealKeyRef.current === healKey) return;
    staleNameHealKeyRef.current = healKey;
    /*
     * ⚠️ `previousClaimantName` and NOTHING else.
     *
     * This used to send the whole sheet plus the name and the case number,
     * and it is the one write on this page that nobody asked for — so it was
     * the one write that could undo what somebody did ask for. Renaming the
     * claimant and editing any other cell in the same save came back with
     * BOTH edits reverted and no error: this effect re-sent eighteen cells it
     * had read a moment earlier, over the save that had just landed.
     *
     * The endpoint keeps the stored sheet when `identity` is omitted, and
     * derives the name it rewrites TO from that sheet — which is the server's
     * own truth, not this component's copy of it. All this needs to say is
     * which stale name the narratives still carry.
     */
    void patchIdentity
      .mutateAsync({ previousClaimantName: stale[0] })
      .catch(() => {
        staleNameHealKeyRef.current = "";
      });
  }, [assessmentId, sourceDrafts, touched, saving, settled, patchIdentity]);

  const patchDrafts = useCallback(
    (updater: (prev: DocumentDrafts) => DocumentDrafts) => {
      setTouched(true);
      setDrafts((prev) => (prev ? updater(prev) : prev));
    },
    [],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        const saveButton = document.getElementById("mlr-save-document");
        saveButton?.click();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const claimantLabel = assessmentClaimantName(assessment);
  const coverClaimant =
    drafts?.identity[MLR_IDENTITY_LABEL.fullName] ||
    formatClaimantNameFromHub(caseHub) ||
    claimantLabel ||
    "Claimant";
  const doctorName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim()
    ? `Dr ${[user?.firstName, user?.lastName].filter(Boolean).join(" ")}`
    : "Examining Physician";
  const reportDateLabel =
    drafts?.identity[MLR_IDENTITY_LABEL.reportDate] ||
    formatMlrShortDate(
      mlr?.statementOfTruthSignedAt ?? assessment.updatedAt ?? new Date(),
    ) ||
    formatMlrShortDate(new Date()) ||
    "—";
  const coverBrand = (() => {
    const refsUnknown: Record<string, unknown> = { ...(caseHub?.refs ?? {}) };
    const agencyName =
      typeof refsUnknown.agencyName === "string"
        ? refsUnknown.agencyName.trim()
        : "";
    const named =
      typeof refsUnknown.coverBrandName === "string"
        ? refsUnknown.coverBrandName.trim()
        : "";
    if (named) return named;
    if (agencyName.toLowerCase().includes("corex")) return "Independent";
    return agencyName || "VOKLIZER";
  })();
  const qualificationsText = htmlToPlain(drafts?.qualifications ?? "");
  const sheetProps = {
    claimantName: coverClaimant,
    doctorName,
    reportDate: reportDateLabel,
    totalPages: TOTAL_PAGES,
  };

  const jumpToId = (id: string) => {
    pauseChromeCollapse();
    setPinnedPage(mlrPageFromAnchorId(id));
    scrollMainToSection(id, { alignBelowId: MLR_EDITOR_CHROME_ID });
  };

  const jumpTo = (number: number) => {
    jumpToId(mlrSectionAnchor(number));
  };

  useEffect(() => {
    if (pinnedPage === null) return;
    const timer = window.setTimeout(() => setPinnedPage(null), 950);
    return () => window.clearTimeout(timer);
  }, [pinnedPage]);

  useEffect(() => {
    const nav = pagesNavRef.current;
    const active = nav?.querySelector("[data-mlr-nav-active='true']");
    if (!(nav && active instanceof HTMLElement)) return;
    const navBox = nav.getBoundingClientRect();
    const itemBox = active.getBoundingClientRect();
    if (itemBox.top < navBox.top) {
      nav.scrollTop -= navBox.top - itemBox.top;
    } else if (itemBox.bottom > navBox.bottom) {
      nav.scrollTop += itemBox.bottom - navBox.bottom;
    }
  }, [activePage]);

  const setStatement = useCallback((id: string, value: string) => {
    patchDrafts((prev) => ({
      ...prev,
      statements: { ...prev.statements, [id]: value },
    }));
  }, [patchDrafts]);

  const onSave = async () => {
    if (!drafts) return;
    const fromName = (
      narrativesNameRef.current ||
      sourceDrafts.identity[MLR_IDENTITY_LABEL.fullName] ||
      ""
    ).trim();
    const toName = (drafts.identity[MLR_IDENTITY_LABEL.fullName] ?? "").trim();
    const toSave = applyClaimantRenameToDrafts(drafts, fromName, toName);
    if (toName) narrativesNameRef.current = toName;
    if (toSave !== drafts) setDrafts(toSave);
    setSaving(true);
    try {
      for (const row of statements) {
        const next = toSave.statements[row.id] ?? "";
        const original = row.details ?? "";
        if (isRichTextEmpty(next) || richTextEqual(next, original)) continue;
        await updateStatement.mutateAsync({ statementId: row.id, details: next });
      }

      const examDirty =
        !richTextEqual(toSave.generalFindings, sourceDrafts.generalFindings) ||
        !richTextEqual(
          toSave.presentationFindings,
          sourceDrafts.presentationFindings,
        ) ||
        textMapDirty(toSave.findingsByInjury, sourceDrafts.findingsByInjury);
      if (examDirty) {
        await saveExamination.mutateAsync({
          generalFindings: toSave.generalFindings,
          presentationFindings: toSave.presentationFindings,
          findingsByInjury: toSave.findingsByInjury,
        });
      }

      const supplementsDirty =
        !richTextEqual(
          toSave.medicalRecordsSummary,
          sourceDrafts.medicalRecordsSummary,
        ) ||
        !richTextEqual(
          toSave.medicalRecordsReview,
          sourceDrafts.medicalRecordsReview,
        ) ||
        !richTextEqual(toSave.prognosisOpinion, sourceDrafts.prognosisOpinion) ||
        !richTextEqual(
          toSave.specialConsiderations,
          sourceDrafts.specialConsiderations,
        ) ||
        !richTextEqual(toSave.futureTreatment, sourceDrafts.futureTreatment) ||
        !richTextEqual(toSave.qualifications, sourceDrafts.qualifications) ||
        !entriesEqual(
          toSave.medicalRecordsEntries,
          sourceDrafts.medicalRecordsEntries,
        ) ||
        !richTextEqual(
          toSave.registrationDetails.gmcNumber,
          sourceDrafts.registrationDetails.gmcNumber,
        ) ||
        !richTextEqual(
          toSave.registrationDetails.mpsNumber,
          sourceDrafts.registrationDetails.mpsNumber,
        ) ||
        !richTextEqual(
          toSave.registrationDetails.icoReference,
          sourceDrafts.registrationDetails.icoReference,
        ) ||
        !richTextEqual(
          toSave.registrationDetails.medcoId,
          sourceDrafts.registrationDetails.medcoId,
        ) ||
        !richTextEqual(
          toSave.registrationDetails.medcoTrainingDate,
          sourceDrafts.registrationDetails.medcoTrainingDate,
        ) ||
        !richTextEqual(
          toSave.registrationDetails.currentPositions,
          sourceDrafts.registrationDetails.currentPositions,
        ) ||
        !richTextEqual(
          toSave.registrationDetails.experience,
          sourceDrafts.registrationDetails.experience,
        ) ||
        JSON.stringify(toSave.caseClassification) !==
          JSON.stringify(sourceDrafts.caseClassification) ||
        JSON.stringify(toSave.exceptionalSeverity) !==
          JSON.stringify(sourceDrafts.exceptionalSeverity);

      if (supplementsDirty) {
        await saveSupplements.mutateAsync({
          medicalRecordsReview: toSave.medicalRecordsReview,
          medicalRecordsSummary: toSave.medicalRecordsSummary,
          medicalRecordsEntries: toSave.medicalRecordsEntries
            .map((entry) => ({
              phase: entry.phase,
              source: entry.source.trim(),
              dateLabel: entry.dateLabel.trim() || undefined,
              narrative: entry.narrative.trim(),
              footnote: entry.footnote.trim() || undefined,
            }))
            .filter(
              (entry) => entry.source && !isRichTextEmpty(entry.narrative),
            ),
          prognosisOpinion: toSave.prognosisOpinion,
          specialConsiderations: toSave.specialConsiderations,
          futureTreatment: toSave.futureTreatment,
          qualifications: toSave.qualifications,
          accountAssessment: toSave.accountAssessment,
          registrationDetails: toSave.registrationDetails,
          caseClassification: toSave.caseClassification,
          /*
           * ⚠️ "" is sent as null, not as a value. The endpoint treats null as
           * "not sent" and preserves what is stored; sending "" would be a
           * deliberate clear. A question the doctor has not reached must not
           * wipe an answer they gave earlier.
           */
          exceptionalWhiplashReported:
            toSave.exceptionalSeverity.exceptionalWhiplashReported || null,
          exceptionalCircumstancesReported:
            toSave.exceptionalSeverity.exceptionalCircumstancesReported || null,
          exceptionalSeverityAgreed:
            toSave.exceptionalSeverity.exceptionalSeverityAgreed || null,
        });
      }

      for (const row of prognosisRows) {
        const next = toSave.prognoses[row.id] ?? "";
        if (isRichTextEmpty(next)) continue;
        const original = row.confirmedText ?? row.aiDraftText ?? "";
        /*
         * ⚠️ `structuredFields` is no longer sent at all — the form that edited
         * it is gone, and the endpoint keeps what is stored when the key is
         * omitted. Serialising a round-tripped copy back on every save was a
         * way for a value nobody touched to be rewritten, and the classification
         * decides a tariff. (18-09-2026)
         */
        const needsWrite =
          !richTextEqual(next, original) || row.status !== "confirmed";
        if (!needsWrite) continue;
        await confirmPrognosis.mutateAsync({
          prognosisId: row.id,
          action: "edit",
          confirmedText: next,
          // Always omitted — see above.
          structuredFields: undefined,
        });
      }

      const staleBefore = inferStaleClaimantNames(
        concatNarrativeDrafts(drafts),
        toName,
      );
      const identityDirty =
        textMapDirty(toSave.identity, sourceDrafts.identity) ||
        staleBefore.length > 0;
      if (identityDirty) {
        await patchIdentity.mutateAsync({
          identity: toSave.identity,
          claimantDisplayName:
            toSave.identity[MLR_IDENTITY_LABEL.fullName] ?? "",
          uniqueReferenceNumber:
            toSave.identity[MLR_IDENTITY_LABEL.caseNumber] ?? "",
          previousClaimantName: staleBefore[0] || fromName,
        });
      }

      toast({ title: "Report saved" });
      setTouched(false);
      onSaved?.();
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Save failed";
      toast({ title: "Could not save report", description: message });
    } finally {
      setSaving(false);
    }
  };

  /**
   * "Agree" is not the same write as saving the draft unchanged: it records
   * `ai_confirmed`, where an edit records `doctor_edited` (ADR-026 §8). Losing
   * this button would have quietly turned every attestation into an edit, so
   * it came onto the paper with the drafts rather than staying in the drawer.
   * (14-09-2026)
   */
  const onAgreePrognosis = async (prognosisId: string) => {
    try {
      await confirmPrognosis.mutateAsync({
        prognosisId,
        action: "agree",
        attestationAccepted: true,
      });
      toast({ title: "Prognosis confirmed" });
      onSaved?.();
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Confirm failed";
      toast({ title: "Could not confirm prognosis", description: message });
    }
  };

  const onGenerate = async () => {
    try {
      const result = await autoGenerate.mutateAsync({});
      toast({
        title: "Report draft generated",
        description: `${result.statementsSaved} statements · ${result.prognosesSeeded} prognosis drafts`,
      });
      onSaved?.();
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Auto-generate failed";
      toast({ title: "Could not generate report draft", description: message });
    }
  };

  /**
   * Correction — the report is being fixed before it goes out.
   *
   * Confirmed, because it withdraws a signature: the doctor has attested to
   * this text and is taking that back. The version they signed is kept either
   * way, so nothing is lost — but it should still be a deliberate act.
   */
  const onCorrect = async () => {
    const ok = window.confirm(
      "Withdraw the signature so this report can be edited?\n\n" +
        "It must be signed again before it can be exported. The version you " +
        "signed stays in the history.",
    );
    if (!ok) return;

    try {
      await voidSignature.mutateAsync({ reason: "correction" });
      toast({
        title: "Signature withdrawn",
        description: "The report is editable again and must be signed a second time.",
      });
      onSaved?.();
    } catch (err) {
      toast({
        title: "Could not withdraw the signature",
        description: err instanceof Error ? err.message : "Please try again",
      });
    }
  };

  /** Amendment — the signed report stands as issued; a new one supersedes it. */
  const onAmend = async () => {
    const ok = window.confirm(
      "Issue an amendment as a new report?\n\n" +
        "The signed report stays exactly as it was issued. The new report " +
        "starts from its account, symptoms and examination — you will need to " +
        "form the prognosis again.",
    );
    if (!ok) return;

    try {
      const created = await amend.mutateAsync({});
      toast({
        title: "Amendment created",
        description: "The signed report is unchanged and stays in the list.",
      });
      navigate(`/medical-assessment/${created.assessmentId}`);
    } catch (err) {
      toast({
        title: "Could not start an amendment",
        description: err instanceof Error ? err.message : "Please try again",
      });
    }
  };

  if (
    examination.isLoading ||
    reportSections.isLoading ||
    supplements.isLoading ||
    prognoses.isLoading ||
    !drafts
  ) {
    return (
      <SkeletonFields fields={6} className="p-6" />
    );
  }

  const busy =
    saving ||
    updateStatement.isPending ||
    saveExamination.isPending ||
    patchIdentity.isPending;

  /*
   * Frozen, not merely signed.
   *
   * `snapshot` is present only when the report was signed AND the section
   * model was frozen behind it (16-09-2026). A report signed before that
   * exists still renders live and is still editable, so it must NOT be shown
   * as frozen — `showSignedEditHint` alone cannot tell the two apart, which is
   * why this reads the sections payload rather than the supplements flag.
   *
   * The server has already stripped every `editable` target from a frozen
   * model, so the page renders read-only without deciding anything here; this
   * is about telling the doctor WHY, and offering the two ways forward.
   */
  const snapshot = reportSections.data?.snapshot ?? null;
  const frozen = Boolean(showSignedEditHint && snapshot);

  // --------------------------------------------------------------------
  // The document, from the model the PDF is drawn from.
  //
  // Sections and their wording arrive assembled; the panel binds the blocks
  // that declare an `editable` target to the local drafts and renders. Four
  // sections still render themselves, because they edit STRUCTURE the print
  // model has no block for: §6's per-source record entries, §8's prognosis
  // rows (each one a separate attestation), §12's classification answers and
  // signature, and §13's registration fields. Their wording still comes from
  // the model wherever the model has it — §12.4 especially, which used to be
  // a hand-typed copy of the sentences the doctor signs. (13-09-2026)
  // --------------------------------------------------------------------
  const modelSections = reportSections.data?.sections ?? [];

  const targetKey = (target: MlrEditableTarget): string => {
    switch (target.kind) {
      case "statement":
        return `statement:${target.id}`;
      case "examination":
        return `examination:${target.field}`;
      case "examinationInjury":
        return `examinationInjury:${target.injuryKey}`;
      case "supplements":
        return `supplements:${target.field}`;
      default:
        return "identity";
    }
  };

  const draftValueFor = (target: MlrEditableTarget): string => {
    switch (target.kind) {
      case "statement":
        return drafts.statements[target.id] ?? "";
      case "examination":
        return drafts[target.field] ?? "";
      case "examinationInjury":
        return drafts.findingsByInjury[target.injuryKey] ?? "";
      case "supplements":
        return drafts[target.field] ?? "";
      default:
        return "";
    }
  };

  const onEditableChange = (target: MlrEditableTarget, next: string) => {
    switch (target.kind) {
      case "statement":
        setStatement(target.id, next);
        return;
      case "examination":
        patchDrafts((prev) => ({ ...prev, [target.field]: next }));
        return;
      case "examinationInjury":
        patchDrafts((prev) => ({
          ...prev,
          findingsByInjury: {
            ...prev.findingsByInjury,
            [target.injuryKey]: next,
          },
        }));
        return;
      case "supplements":
        patchDrafts((prev) => ({ ...prev, [target.field]: next }));
        return;
      default:
    }
  };

  const binding: MlrBlockBinding = {
    valueOf: draftValueFor,
    // The model carries the PERSISTED source for every editable block, so the
    // unsaved-change highlight needs no second copy of the server state.
    originalOf: (target) => ("html" in target ? target.html : ""),
    onChange: onEditableChange,
    identityValue: (row) => drafts.identity[row.key ?? row.label] ?? row.value,
    onIdentityChange: (label, value) =>
      patchDrafts((prev) => ({
        ...prev,
        identity: patchIdentityValue(prev.identity, label, value),
      })),
    /*
     * The two answers to a withheld finding.
     *
     * The question names the CLAIM, not the row: a doctor can answer "did I
     * say shoulders?" from memory of the room, and cannot answer "is statement
     * stmt_3 right?". Removal is destructive and asks; accepting is not, and
     * goes straight through.
     *
     * Neither appears on a frozen report — the server strips `editable` from a
     * frozen model, so the notice renders with no statement id and no actions.
     */
    onVerify: (statementId, claim) => {
      void (async () => {
        try {
          await verifyStatement.mutateAsync(statementId);
          toast({
            title: "Finding kept",
            description: `"${claim.value}" will now print in the report.`,
          });
          onSaved?.();
        } catch (err) {
          toast({
            title: "Could not confirm it",
            description: err instanceof Error ? err.message : undefined,
            variant: "destructive",
          });
        }
      })();
    },
    onRemove: (statementId, claim) => {
      const ok = window.confirm(
        `Remove "${claim.value}" from the report?\n\n` +
          "The recording does not support it. This cannot be undone.",
      );
      if (!ok) return;

      void (async () => {
        try {
          await deleteStatement.mutateAsync(statementId);
          toast({ title: "Finding removed" });
          onSaved?.();
        } catch (err) {
          toast({
            title: "Could not remove it",
            description: err instanceof Error ? err.message : undefined,
            variant: "destructive",
          });
        }
      })();
    },
    onIdentityBlur: (label) => {
      if (label !== MLR_IDENTITY_LABEL.fullName) return;
      patchDrafts((prev) => {
        const from = narrativesNameRef.current;
        const to = (prev.identity[MLR_IDENTITY_LABEL.fullName] ?? "").trim();
        const next = applyClaimantRenameToDrafts(prev, from, to);
        if (to) narrativesNameRef.current = to;
        return next;
      });
    },
  };

  /**
   * §7 — a finding nobody has written has no block to render, so the model
   * lists the slots an editor may start. Anything already present as an
   * editable block is skipped, or an empty §7 would offer "General" twice.
   */
  const renderExaminationSlots = (section: MlrReportSection) => {
    const present = new Set(
      section.blocks
        .map((block) => ("editable" in block ? block.editable : undefined))
        .filter(Boolean)
        .map((target) => targetKey(target as MlrEditableTarget)),
    );
    const slots = (section.editorSlots ?? []).filter(
      (slot) =>
        !present.has(
          slot.kind === "examination"
            ? `examination:${slot.field}`
            : `examinationInjury:${slot.injuryKey}`,
        ),
    );
    if (slots.length === 0) return null;
    return (
      <>
        {slots.map((slot) => {
          const target: MlrEditableTarget =
            slot.kind === "examination"
              ? { kind: "examination", field: slot.field, html: slot.html }
              : {
                  kind: "examinationInjury",
                  injuryKey: slot.injuryKey,
                  html: slot.html,
                };
          const value = draftValueFor(target);
          return (
            <div key={`${slot.kind}-${slot.label}`}>
              <MlrSubheading>{slot.label}</MlrSubheading>
              <DocParagraph
                value={value}
                onChange={(next) => onEditableChange(target, next)}
                ariaLabel={slot.label}
                placeholder={`Examination finding for ${slot.label}…`}
                dirty={!richTextEqual(value, slot.html)}
              />
            </div>
          );
        })}
      </>
    );
  };

  const renderRecordsSection = (section: MlrReportSection) => (
    <>
      {/*
          Which records, before what they said.

          ⚠️ §6 used to open with a fixed list of five record types and a claim
          they were "legible and complete", generated from one Case Hub boolean
          meaning only "something arrived". This is the doctor's own answer, and
          §6 prints nothing from it until they confirm. A frozen report returns
          `recordsReview: null` and the panel does not render. (17-09-2026)
       */}
      {section.recordsReview !== undefined && section.recordsReview !== null ? (
        <MlrRecordsReviewPanel
          assessmentId={assessmentId}
          review={section.recordsReview}
          onSaved={onSaved}
        />
      ) : null}
      <MlrSubheading>6.1 Medical Records / Documents Review</MlrSubheading>
      {(["pre", "post"] as const).map((phase) => {
        const phaseEntries = drafts.medicalRecordsEntries
          .map((entry, index) => ({ entry, index }))
          .filter((item) => item.entry.phase === phase);
        if (phaseEntries.length === 0) return null;
        const phaseLabel =
          phase === "pre"
            ? "Review of Pre-Incident Medical Records"
            : "Review of Post-Incident Medical Records";
        const sourceNumber = phase === "pre" ? "6.1.1" : "6.1.2";
        return (
          <div key={phase}>
            <MlrSubheading>{phaseLabel}</MlrSubheading>
            {phaseEntries.map(({ entry, index }) => (
              <div key={`records-${index}`}>
                <MlrNumberLabel>{sourceNumber}</MlrNumberLabel>
                <MlrStaticParagraph>{entry.source}</MlrStaticParagraph>
                {entry.dateLabel ? (
                  <MlrStaticParagraph>{entry.dateLabel}</MlrStaticParagraph>
                ) : null}
                <DocParagraph
                  value={entry.narrative}
                  onChange={(next) =>
                    patchDrafts((prev) => ({
                      ...prev,
                      medicalRecordsEntries: prev.medicalRecordsEntries.map(
                        (item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, narrative: next }
                            : item,
                      ),
                    }))
                  }
                  ariaLabel={`Medical records entry ${index + 1}`}
                  dirty={
                    !richTextEqual(
                      entry.narrative,
                      sourceDrafts.medicalRecordsEntries[index]?.narrative ?? "",
                    )
                  }
                />
              </div>
            ))}
          </div>
        );
      })}
      <MlrSubheading>6.2  Medical Records Summary:</MlrSubheading>
      <DocParagraph
        value={drafts.medicalRecordsSummary}
        onChange={(next) =>
          patchDrafts((prev) => ({ ...prev, medicalRecordsSummary: next }))
        }
        ariaLabel="Medical records summary"
        placeholder="I have reviewed the claimant's GP medical records…"
        dirty={
          !richTextEqual(
            drafts.medicalRecordsSummary,
            sourceDrafts.medicalRecordsSummary,
          )
        }
        minRows={3}
      />
      {!drafts.medicalRecordsEntries.length && drafts.medicalRecordsReview ? (
        <>
          <MlrSubheading>Records reviewed</MlrSubheading>
          <DocParagraph
            value={drafts.medicalRecordsReview}
            onChange={(next) =>
              patchDrafts((prev) => ({ ...prev, medicalRecordsReview: next }))
            }
            ariaLabel="Medical records review"
            dirty={
              !richTextEqual(
                drafts.medicalRecordsReview,
                sourceDrafts.medicalRecordsReview,
              )
            }
          />
        </>
      ) : null}
      {/*
          ⚠️ The records import used to sit here — "From Case Hub", "Upload
          records PDF", "Paste chronology".

          Removed 18-09-2026. Records arrive from the instructing party BEFORE
          the consultation, and the Case Hub seed already runs on every
          generation (`seedMlrRecordsFromCaseHub` in `autoGenerate.service.mjs`),
          so the buttons re-ran something automatic and put a file-upload flow
          in the middle of reading a report. Nobody uploads records while
          writing one.

          The ingest endpoints are untouched — if records ever need attaching by
          hand, that belongs at intake or on the Case Hub, not on the page the
          doctor reads.
       */}
    </>
  );

  const renderPrognosisSection = (section: MlrReportSection) => {
    // Everything up to the first confirmed injury heading is §8.1 plus the
    // §8.2 lead-in — the rows themselves are rendered below so each stays
    // editable and attestable.
    const firstInjury = section.blocks.findIndex(
      (block) => block.type === "numberedHeading",
    );
    const lead =
      firstInjury === -1 ? section.blocks : section.blocks.slice(0, firstInjury);
    const confirmed = prognosisRows.filter((row) => row.status === "confirmed");
    const pending = prognosisRows.filter((row) => row.status !== "confirmed");

    const prognosisEditor = (row: (typeof prognosisRows)[number], label: string) => {
      const value = drafts.prognoses[row.id] ?? "";
      const original = row.confirmedText ?? row.aiDraftText ?? "";
      return (
        <div key={row.id}>
          <MlrSubheading>{label}</MlrSubheading>
          <DocParagraph
            value={value}
            onChange={(next) =>
              patchDrafts((prev) => ({
                ...prev,
                prognoses: { ...prev.prognoses, [row.id]: next },
              }))
            }
            ariaLabel={`Prognosis ${row.injuryLabel}`}
            dirty={
              !richTextEqual(value, original) || row.status !== "confirmed"
            }
            minRows={4}
          />
          {/*
              The one thing the recording did not settle, asked once.

              ⚠️ Not a permanent control — the backend stops sending it the
              moment a rule can decide or someone answers. A classification
              picker that is always on screen is the desktop tool this replaces.
              (18-09-2026)
           */}
          {row.openQuestion ? (
            <div className="mb-2 rounded-md border border-primary/40 bg-primary/5 p-3">
              <p className="text-sm font-semibold">{row.openQuestion.question}</p>
              {row.openQuestion.hint ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {row.openQuestion.hint}
                </p>
              ) : null}
              {/* A scale gets an input; a choice gets buttons. A recovery
                  period is a point the doctor picks, not one of two answers.
                  (18-09-2026) */}
              {row.openQuestion.kind === "number" ? (
                <form
                  className="mt-2 flex flex-wrap items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const input = new FormData(event.currentTarget).get("value");
                    const value = Number(input);
                    if (!Number.isFinite(value)) return;
                    void answerDecision.mutateAsync({
                      prognosisId: row.id,
                      decision: row.openQuestion!.decision,
                      value,
                    });
                  }}
                >
                  <input
                    name="value"
                    type="number"
                    min={row.openQuestion.min ?? undefined}
                    max={row.openQuestion.max ?? undefined}
                    placeholder="Months"
                    aria-label={row.openQuestion.question}
                    className="w-28 rounded-md border bg-background px-2 py-1 text-sm"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    variant="outline"
                    disabled={answerDecision.isPending}
                  >
                    Save
                  </Button>
                </form>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {row.openQuestion.options.map((option) => (
                    <Button
                      key={option.value}
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={answerDecision.isPending}
                      onClick={() =>
                        void answerDecision.mutateAsync({
                          prognosisId: row.id,
                          decision: row.openQuestion!.decision,
                          value: option.value,
                        })
                      }
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {/*
              Why this injury is classified the way it is. The backend withholds
              it when the DOCTOR stated the classification — their word is the
              authority and needs no citation. (18-09-2026)
           */}
          {row.classification ? (
            <p className="mb-2 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Classified{" "}
                <span className="font-medium text-foreground">
                  {row.classification.value}
                </span>{" "}
                because {describeBecause(row.classification.because)} —{" "}
                {row.classification.cite}
                {/*
                    The doctor outranks the rulebook. One decision, changed
                    beside the reason given for it — not a field in a form
                    whose heading says nothing about why it holds that value.
                 */}
                {" · "}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-foreground"
                  disabled={answerDecision.isPending}
                  onClick={() =>
                    void answerDecision.mutateAsync({
                      prognosisId: row.id,
                      decision: "prognosis.whiplashType",
                      value:
                        row.classification!.value === "whiplash"
                          ? "non-whiplash"
                          : "whiplash",
                    })
                  }
                >
                  Change to{" "}
                  {row.classification.value === "whiplash"
                    ? "non-whiplash"
                    : "whiplash"}
                </button>
              </span>
            </p>
          ) : null}

          {/*
              ⚠️ A five-field form used to sit here — causation, classification,
              expected recovery, treatment, referrals — under every injury, on
              every case. Removed 18-09-2026.

              It was the desktop tool it replaces: it asked the doctor to fill
              in what the consultation had already said. And those fields do
              only one thing — seed the AI draft SENTENCE. They never reach the
              GRIP export, and once the doctor has the sentence, editing them
              changes nothing that prints. The prose above is the correction
              path for the wording.

              The one exception is the classification, because the report
              ASSERTS it and a tariff follows from it. That is a single
              decision, and it is changed in place beside the reason for it —
              see the note above.
           */}
        </div>
      );
    };

    return (
      <>
        <MlrDocBlocks blocks={lead} binding={binding} />
        {confirmed.map((row, index) =>
          prognosisEditor(row, `8.2.${index + 1}  ${row.injuryLabel}`),
        )}
        {pending.length > 0 ? (
          <div className="mt-6 border-l-2 border-dashed border-[#b8b8b0] pl-3">
            {/* ADR-026 §8: an AI draft is never part of the report. It used to
                render here as body text, so the panel showed a document the
                PDF would not produce. */}
            <p className="font-[family-name:Times_New_Roman,Times,serif] text-[9pt] italic text-[#666666]">
              Not in the report until you confirm it — Tools → Prognosis
              structured fields, or the phone.
            </p>
            {pending.map((row) => (
              <div key={row.id}>
                {prognosisEditor(row, `Draft — ${row.injuryLabel}`)}
                <button
                  type="button"
                  onClick={() => void onAgreePrognosis(row.id)}
                  disabled={confirmPrognosis.isPending}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-none border border-[#1a1a1a] bg-transparent px-3 py-1 text-[9.5pt] font-bold text-[#1a1a1a] transition-colors hover:bg-[#1a1a1a] hover:text-white disabled:cursor-not-allowed disabled:border-[#c8c8c8] disabled:text-[#9a9a9a] disabled:hover:bg-transparent"
                  style={{
                    fontFamily: '"Times New Roman", Times, Georgia, serif',
                  }}
                >
                  <Check className="h-3 w-3" />
                  Agree &amp; add to report
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  };

  /**
   * §9 — the exceptional-circumstances questions as controls.
   *
   * ⛔ The questions, their wording, their numbering and WHICH of them apply
   * all come from the section the backend built. The panel kept its own copy
   * of §12.1's questions with a hardcoded "12.1" once before; it went stale
   * the moment a report shape dropped that block, and this is the same shape
   * of mistake one section higher up. (19-09-2026)
   */
  const renderSpecialSection = (section: MlrReportSection) => {
    const answers = section.exceptionalSeverity;

    /* The doctor's own free text has replaced the questions — show it as it
       has always been shown, and do not offer controls for questions the
       report is no longer asking. */
    if (!answers || answers.overridden) {
      return <MlrDocBlocks blocks={section.blocks} binding={binding} />;
    }

    /* The subheadings are the backend's; only the paragraphs holding the
       questions are replaced by controls. */
    return (
      <>
        <MlrDocBlocks
          blocks={section.blocks.filter(
            (block) =>
              !(block.type === "paragraphs" && block.role === "exceptionalQuestions"),
          )}
          binding={binding}
        />
        {answers.questions.map((entry) => (
          <ClassificationAnswer
            key={entry.key}
            question={entry.question}
            answered={drafts.exceptionalSeverity[entry.key] ?? ""}
            /*
             * ⚠️ What the report PRINTS, which for the first two questions is
             * a derivation rather than a silence. Passing `undefined` when the
             * doctor has not answered would tell them "Not answered — prints
             * blank" under a question the report answers "No".
             *
             * The doctor's question is the one that really is blank until they
             * press a button, and it arrives as null.
             */
            resolved={
              entry.value
                ? {
                    value: entry.value,
                    /* null renders as "Derived" — the reason box, not the
                       rulebook or intake, is what settled these two. */
                    source: entry.answered ? "doctor" : null,
                  }
                : undefined
            }
            onAnswer={(value) =>
              patchDrafts((prev) => ({
                ...prev,
                exceptionalSeverity: {
                  ...prev.exceptionalSeverity,
                  [entry.key]: value,
                },
              }))
            }
          />
        ))}
      </>
    );
  };

  const renderDeclarationSection = (section: MlrReportSection) => {
    const declaration = section.blocks.find(
      (block) => block.type === "declaration",
    );
    const statementOfTruth =
      declaration && declaration.type === "declaration"
        ? (declaration.statementOfTruth ?? [])
        : [];
    /* What the rulebook and intake settled, and what neither did. The doctor's
       own answer lives in the drafts and outranks all of it. */
    const resolvedClassification: Record<string, MlrClassificationAnswer> =
      declaration && declaration.type === "declaration"
        ? (declaration.caseClassification ?? {})
        : {};
    /*
     * ⚠️ This section's number and contents come from the report's SHAPE, not
     * from literals. §12.1 is the whiplash-tariff triage and exists only where
     * the tariff can apply — on an exposure report (housing disrepair) the PDF
     * omits it and everything below it moves up, so hardcoding "12.1"/"12.4"
     * here would show a doctor questions the document does not ask, under
     * numbers it does not use. The backend decides; this reads its answer.
     * (19-09-2026, docs/product/mlr-case-types.md)
     */
    const declarationBlock =
      declaration && declaration.type === "declaration" ? declaration : null;
    const sectionNumber = declarationBlock?.sectionNumber ?? section.number;
    const showClassification = declarationBlock?.includeClassification !== false;
    // Positions, not names: dropping the tariff block moves the rest up one.
    const statementOfTruthNumber = `${sectionNumber}.${showClassification ? 4 : 3}`;

    return (
      <>
        <MlrDocBlocks
          blocks={section.blocks}
          binding={binding}
          skip={["declaration"]}
        />
        {showClassification ? (
          <>
            <MlrSubheading>{`${sectionNumber}.1`}</MlrSubheading>
            {CLASSIFICATION_QUESTIONS.map(([key, question]) => (
              <ClassificationAnswer
                key={key}
                question={question}
                answered={drafts.caseClassification[key]}
                resolved={resolvedClassification[key]}
                onAnswer={(value) =>
                  patchDrafts((prev) => ({
                    ...prev,
                    caseClassification: {
                      ...prev.caseClassification,
                      [key]: value,
                    },
                  }))
                }
              />
            ))}
          </>
        ) : null}
        <MlrSubheading>{`${statementOfTruthNumber}  Statement of Truth`}</MlrSubheading>
        {/* The sentences being signed come from the same builder the PDF
            prints (BACKEND export/statementOfTruth.mjs). A Sign button beside
            a second, hand-typed copy asks for a signature on different
            words. */}
        {statementOfTruth.map((para) => (
          <MlrStaticParagraph key={para.slice(0, 32)}>{para}</MlrStaticParagraph>
        ))}
        <p className="mt-6 font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] font-bold text-[#1a1a1a]">
          Date
        </p>
        <p className="font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] text-[#1a1a1a]">
          {mlr?.statementOfTruthConfirmed
            ? reportDateLabel
            : "_______________________________"}
        </p>
        <p className="mt-4 font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] font-bold text-[#1a1a1a]">
          Signature
        </p>
        <p className="font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] text-[#1a1a1a]">
          {mlr?.statementOfTruthSignerName || doctorName}
        </p>
        {qualificationsText ? (
          <p className="font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] text-[#1a1a1a]">
            {qualificationsText}
          </p>
        ) : null}
        <div className="mt-6">
          <StatementOfTruthReview
            assessmentId={assessmentId}
            embedded
            unansweredClassification={CLASSIFICATION_QUESTIONS.filter(
              ([key]) =>
                !(
                  drafts.caseClassification[key] ||
                  resolvedClassification[key]?.value
                ),
            ).map(([, question]) => question)}
          />
        </div>
      </>
    );
  };

  const renderQualificationsSection = () => (
    <>
      <MlrSubheading>Registration and Licensing Details:</MlrSubheading>
      {(
        [
          ["gmcNumber", "GMC reference number"],
          ["mpsNumber", "MPS Number"],
          ["icoReference", "ICO Registration Reference"],
          ["medcoId", "MedCo ID"],
          [
            "medcoTrainingDate",
            "MedCo Mandatory Accredited Training Achieved on",
          ],
        ] as const
      ).map(([key, label]) => (
        <div key={key} className="mt-1">
          <span className="font-[family-name:Times_New_Roman,Times,serif] text-[10.5pt] text-[#1a1a1a]">
            {label} -{" "}
          </span>
          <DocParagraph
            value={drafts.registrationDetails[key]}
            onChange={(next) =>
              patchDrafts((prev) => ({
                ...prev,
                registrationDetails: {
                  ...prev.registrationDetails,
                  [key]: next,
                },
              }))
            }
            ariaLabel={label}
            minRows={1}
            dirty={
              !richTextEqual(
                drafts.registrationDetails[key],
                sourceDrafts.registrationDetails[key],
              )
            }
          />
        </div>
      ))}
      <MlrSubheading>Current Positions Held:</MlrSubheading>
      <DocParagraph
        value={drafts.registrationDetails.currentPositions}
        onChange={(next) =>
          patchDrafts((prev) => ({
            ...prev,
            registrationDetails: {
              ...prev.registrationDetails,
              currentPositions: next,
            },
          }))
        }
        ariaLabel="Current positions held"
        minRows={1}
        dirty={
          !richTextEqual(
            drafts.registrationDetails.currentPositions,
            sourceDrafts.registrationDetails.currentPositions,
          )
        }
      />
      <MlrSubheading>Qualification:</MlrSubheading>
      <DocParagraph
        value={drafts.qualifications}
        onChange={(next) =>
          patchDrafts((prev) => ({ ...prev, qualifications: next }))
        }
        ariaLabel="Qualifications"
        placeholder="e.g. MBBS, MRCGP"
        dirty={
          !richTextEqual(drafts.qualifications, sourceDrafts.qualifications)
        }
        minRows={1}
      />
      <MlrSubheading>Experience:</MlrSubheading>
      <DocParagraph
        value={drafts.registrationDetails.experience}
        onChange={(next) =>
          patchDrafts((prev) => ({
            ...prev,
            registrationDetails: {
              ...prev.registrationDetails,
              experience: next,
            },
          }))
        }
        ariaLabel="Medico-legal experience"
        dirty={
          !richTextEqual(
            drafts.registrationDetails.experience,
            sourceDrafts.registrationDetails.experience,
          )
        }
      />
      {/*
          ⚠️ "Fetch my details from MEDYX" used to sit here. Removed 18-09-2026.

          §13 is the doctor's registration and qualifications, and this product
          does not hold them — they belong to MEDYX and arrive over its API,
          keyed on the signed-in email. That lookup already ran on a Case Hub
          pull and now runs on every generation too
          (`seedMlrRegistrationFromDoctor` in `autoGenerate.service.mjs`), so
          the button re-ran something automatic.

          A button also implied the opposite of the truth: that the details are
          ours to fetch on request, rather than another system's that we are
          given. They are simply here.
       */}
    </>
  );

  /*
   * ⚠️ Dispatched by section **id**, not by its printed number.
   *
   * This switched on `section.number` — correct for exactly one report shape.
   * A shape that drops Special Considerations and Safety Restraints renumbers
   * everything below them, so Declaration becomes 10 and Qualifications 11,
   * and `case 12` / `case 13` stopped matching. The declaration block renders
   * as `null` in the generic path, so on an exposure, trip, work or pedestrian
   * report the panel silently showed **no classification questions, no
   * Statement of Truth and no signature block at all**.
   *
   * The backend split identity from presentation for this exact reason —
   * `push()` takes an id and the number is wherever the shape puts it. The
   * panel was still reading the number. (19-09-2026,
   * docs/product/mlr-case-types.md)
   */
  const renderSectionBody = (section: MlrReportSection) => {
    switch (section.id) {
      case "records":
        return renderRecordsSection(section);
      case "exam":
        return (
          <>
            <MlrDocBlocks blocks={section.blocks} binding={binding} />
            {renderExaminationSlots(section)}
          </>
        );
      case "prognosis":
        return renderPrognosisSection(section);
      case "special":
        return renderSpecialSection(section);
      case "declaration":
        return renderDeclarationSection(section);
      case "qualifications":
        return renderQualificationsSection();
      default:
        return <MlrDocBlocks blocks={section.blocks} binding={binding} />;
    }
  };

  return (
    <MlrRichTextProvider>
    <>
      {/*
          The one thing the paper questionnaire still did better.

          A form is a checklist: every box is at least seen, even when the
          answer is "no". A conversation has no such guarantee, and a report
          printed without a question having been asked looks exactly like one
          where the answer was no. Above the document, because it describes
          what is NOT in it. (16-09-2026)
       */}
      {coverage.data &&
      ((coverage.data.coverage.notAsked ?? coverage.data.coverage.missingCount) >
        0 ||
        coverage.data.warnings.length > 0) ? (
        <MlrCoverageCallout
          coverage={coverage.data.coverage}
          warnings={coverage.data.warnings}
        />
      ) : null}

      {frozen ? (
        <div className="mx-4 mb-4 mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 md:mx-5">
          <Lock className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-medium">
              {snapshot?.signerName
                ? `Signed by ${snapshot.signerName}`
                : "Signed"}
              {snapshot?.signedAt
                ? ` on ${new Date(String(snapshot.signedAt)).toLocaleDateString("en-GB")}`
                : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              This is the report exactly as signed, and it cannot change. To
              change it, either withdraw the signature and correct it, or issue
              an amendment as a new report.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={voidSignature.isPending || amend.isPending}
              onClick={() => void onCorrect()}
            >
              {voidSignature.isPending ? (
                <LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Correct it
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={voidSignature.isPending || amend.isPending}
              onClick={() => void onAmend()}
            >
              {amend.isPending ? (
                <LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Amend it
            </Button>
          </div>
        </div>
      ) : null}
      <MlrEditorChromePortal>
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 text-sm">
          {dirty ? (
            <span className="font-medium">Unsaved changes</span>
          ) : needsPrognosisConfirm ? (
            <span className="text-muted-foreground">
              Draft prognosis on the page — Save to include it in the PDF
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
              Document saved
            </span>
          )}
          {/* Until 16-09-2026 this said "Signed — re-export if you change
              wording", which is now actively wrong: a write to a signed report
              is refused, and the document on screen is the frozen snapshot.
              The banner above carries the two ways forward. */}
          {frozen ? (
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" aria-hidden />
              Signed — this is the report as signed
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MlrFormatToolbar />
          {statements.length === 0 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!assessment.sessionId || autoGenerate.isPending}
              onClick={() => void onGenerate()}
            >
              {autoGenerate.isPending ? (
                <LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-3.5 w-3.5" />
              )}
              Generate draft
            </Button>
          ) : null}
          <Button
            id="mlr-save-document"
            type="button"
            size="sm"
            disabled={(!dirty && !needsPrognosisConfirm) || busy}
            onClick={() => void onSave()}
          >
            {busy ? (
              <LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="mr-2 h-3.5 w-3.5" />
            )}
            Save document
          </Button>
        </div>
      </div>
      </MlrEditorChromePortal>

    <div className="lg:grid lg:grid-cols-[13rem_minmax(0,1fr)]">
      <nav
        ref={pagesNavRef}
        aria-label="Report sections"
        className="border-b border-border/60 bg-background px-3 py-3 lg:sticky lg:top-28 lg:z-10 lg:max-h-[calc(100vh-12rem)] lg:self-start lg:overflow-y-auto lg:border-b-0 lg:border-r lg:px-4 lg:py-5"
      >
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Pages
        </p>
        <ol className="flex gap-1 overflow-x-auto lg:block lg:space-y-0.5 lg:overflow-visible">
          <li className="shrink-0">
            <button
              type="button"
              aria-current={activePage === "cover" ? "true" : undefined}
              data-mlr-nav-active={activePage === "cover" ? "true" : undefined}
              onClick={() => jumpToId("mlr-cover")}
              className={mlrNavItemClass(activePage === "cover")}
            >
              Cover
            </button>
          </li>
          <li className="shrink-0">
            <button
              type="button"
              aria-current={activePage === "contents" ? "true" : undefined}
              data-mlr-nav-active={
                activePage === "contents" ? "true" : undefined
              }
              onClick={() => jumpToId("mlr-contents")}
              className={mlrNavItemClass(activePage === "contents")}
            >
              Contents
            </button>
          </li>
          {modelSections.map((section) => {
            const active = activePage === section.number;
            return (
              <li key={section.id} className="shrink-0">
                <button
                  type="button"
                  aria-current={active ? "true" : undefined}
                  data-mlr-nav-active={active ? "true" : undefined}
                  onClick={() => jumpTo(section.number)}
                  className={mlrNavItemClass(active)}
                >
                  <span className={active ? "font-semibold" : "font-medium"}>
                    {section.number}.
                  </span>{" "}
                  <span className="hidden lg:inline">{section.title}</span>
                  <span className="lg:hidden">§{section.number}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="min-w-0">
        <div
          ref={setPaperStack}
          className="space-y-6 bg-[#cfcfc8] p-3 dark:bg-app-quaternary sm:p-6 md:p-8"
        >
          {statements.length === 0 ? (
            <p
              // On the desk, not the paper: the desk flips with the theme, so
              // this line takes a token and not the sheet's fixed ink.
              className="mx-auto max-w-[210mm] text-center text-xs text-muted-foreground"
            >
              Empty draft — use <strong>Generate draft</strong> above to build
              it from the recording, or type straight into the paper. Layout
              matches the exported MLR.
            </p>
          ) : null}

          <MlrCoverPage
            brand={coverBrand}
            claimantName={coverClaimant}
            doctorName={doctorName}
            qualifications={qualificationsText}
            reportDate={reportDateLabel}
            pageNumber={1}
            totalPages={TOTAL_PAGES}
          />

          <MlrContentsPage
            onJump={jumpTo}
            pageNumber={2}
            sections={modelSections}
            {...sheetProps}
          />

          {modelSections.map((section) => (
            <MlrSheet
              key={section.number}
              id={mlrSectionAnchor(section.number)}
              pageNumber={sectionPageNumber(section.number)}
              {...sheetProps}
            >
              <MlrSectionHeading
                number={section.number}
                title={section.title}
              />
              {renderSectionBody(section)}
            </MlrSheet>
          ))}
        </div>
      </div>
    </div>
    </>
    </MlrRichTextProvider>
  );
}
